/*
 *
 * AdminPage / Users / UserActions
 *
 * Superadmin mutations on a single user, rendered beneath the read-only detail.
 * Backed by the admin user-mutation endpoints (all super_admin-gated server-side
 * — the client affordances are UX only):
 *   - PATCH /api/v3/admin/users/:id          → edit role (+ name/email/company)
 *   - POST  /api/v3/admin/users/:id/assign_unlimited_plan → revenue-adjacent
 *   - DELETE /api/v3/admin/users/:id         → destructive
 *
 * The destructive + revenue-adjacent actions require an explicit in-UI confirm
 * step (no native window.confirm) so the intent is unambiguous and testable.
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { t } from "copy/t";

import {
  useUpdateAdminUser,
  useDeleteAdminUser,
  useAssignUnlimitedPlan,
} from "../api/mutations";
import { ROLE_OPTIONS, roleLabel } from "./roles";
import {
  ActionsWrapper,
  ActionRow,
  FieldLabel,
  DetailLabel,
  RoleSelect,
  SubmitButton,
  DangerButton,
  SecondaryButton,
  ConfirmRow,
  FormError,
} from "./styledComponents";

// Map the rails error payload to a readable string for the inline banner.
const formatError = (err, fallback) => {
  if (err?.errors && typeof err.errors === "object") {
    const parts = Object.entries(err.errors).map(
      ([field, msgs]) => `${field} ${Array.isArray(msgs) ? msgs.join(", ") : msgs}`
    );
    if (parts.length) return parts.join("; ");
  }
  return err?.error || fallback;
};

const UserActions = ({ user }) => {
  const navigate = useNavigate();
  const id = user.id;

  // Role editor — seeded from the user's current role (enum string → its int).
  // Look up against ROLE_OPTIONS (the FULL enum, incl. `lapsed`), not the
  // assignable subset: a lapsed user's current role must map to a real option
  // so the controlled select shows their actual role rather than silently
  // defaulting to the first option (which could let an admin persist
  // permission_role: 0 for a lapsed user without realizing it).
  const currentRoleValue =
    ROLE_OPTIONS.find((r) => r.key === user.permission_role)?.value ?? "";
  const [role, setRole] = useState(String(currentRoleValue));
  const [error, setError] = useState(null);

  // Inline confirm gates for the two non-reversible actions.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmUnlimited, setConfirmUnlimited] = useState(false);

  const updateUser = useUpdateAdminUser(id);
  const deleteUser = useDeleteAdminUser(id);
  const assignUnlimited = useAssignUnlimitedPlan(id);

  const roleChanged = role !== "" && Number(role) !== currentRoleValue;

  const onSaveRole = () => {
    setError(null);
    updateUser.mutate(
      { permission_role: Number(role) },
      {
        onSuccess: (updated) =>
          toast.success(t("toast.admin.roleUpdated", { role: roleLabel(updated.permission_role) })),
        onError: (err) => {
          const message = formatError(err, "Couldn’t update the role.");
          setError(message);
          toast.error(message);
        },
      }
    );
  };

  const onConfirmUnlimited = () => {
    setError(null);
    assignUnlimited.mutate(undefined, {
      onSuccess: () => {
        toast.success(t("toast.admin.unlimitedPlanAssigned"));
        setConfirmUnlimited(false);
      },
      onError: (err) => {
        const message = formatError(err, "Couldn’t assign the unlimited plan.");
        setError(message);
        toast.error(message);
        setConfirmUnlimited(false);
      },
    });
  };

  const onConfirmDelete = () => {
    setError(null);
    deleteUser.mutate(undefined, {
      onSuccess: () => {
        toast.success(t("toast.admin.userDeleted"));
        navigate("/admin/users");
      },
      onError: (err) => {
        const message = formatError(err, "Couldn’t delete the user.");
        setError(message);
        toast.error(message);
        setConfirmDelete(false);
      },
    });
  };

  return (
    <ActionsWrapper>
      <h2>Manage</h2>
      {error && <FormError role="alert">{error}</FormError>}

      <ActionRow>
        <FieldLabel htmlFor="edit-user-role">Role</FieldLabel>
        <RoleSelect
          id="edit-user-role"
          value={role}
          onChange={(e) => setRole(e.target.value)}
        >
          {ROLE_OPTIONS.map((r) => (
            <option key={r.key} value={r.value}>
              {r.label}
            </option>
          ))}
        </RoleSelect>
        <SubmitButton
          type="button"
          onClick={onSaveRole}
          disabled={!roleChanged || updateUser.isLoading}
        >
          {updateUser.isLoading ? "Saving…" : "Save role"}
        </SubmitButton>
      </ActionRow>

      <ActionRow>
        <DetailLabel>Unlimited plan</DetailLabel>
        {confirmUnlimited ? (
          <ConfirmRow>
            <span>Assign the unlimited annual plan? This resets the subscription.</span>
            <SubmitButton
              type="button"
              onClick={onConfirmUnlimited}
              disabled={assignUnlimited.isLoading}
            >
              {assignUnlimited.isLoading ? "Assigning…" : "Confirm"}
            </SubmitButton>
            <SecondaryButton type="button" onClick={() => setConfirmUnlimited(false)}>
              Cancel
            </SecondaryButton>
          </ConfirmRow>
        ) : (
          <SecondaryButton type="button" onClick={() => setConfirmUnlimited(true)}>
            Assign unlimited plan
          </SecondaryButton>
        )}
      </ActionRow>

      <ActionRow>
        <DetailLabel>Danger zone</DetailLabel>
        {confirmDelete ? (
          <ConfirmRow>
            <span>Delete this user permanently? This can’t be undone.</span>
            <DangerButton
              type="button"
              onClick={onConfirmDelete}
              disabled={deleteUser.isLoading}
            >
              {deleteUser.isLoading ? "Deleting…" : "Confirm delete"}
            </DangerButton>
            <SecondaryButton type="button" onClick={() => setConfirmDelete(false)}>
              Cancel
            </SecondaryButton>
          </ConfirmRow>
        ) : (
          <DangerButton type="button" onClick={() => setConfirmDelete(true)}>
            Delete user
          </DangerButton>
        )}
      </ActionRow>
    </ActionsWrapper>
  );
};

export default UserActions;
